import React, { useState, useEffect, useRef, useCallback } from "react";
import { Button } from "primereact/button";
import { InputTextarea } from "primereact/inputtextarea";
import ModalBase from "./ModalBase";
import PropTypes from "prop-types";
import showcaseConfig from "../ChartSelectionModal/showcaseVideos.json";
import "./RatingModal.css";

const SHOWCASE_VIDEOS = showcaseConfig.videos;

/**
 * Star Rating Modal Component
 * Shows version features (videos) + star rating with optional feedback
 */
const RatingModal = ({ visible, onHide, onSubmit, isLoading = false }) => {
  const [rating, setRating] = useState(0);
  const [hoveredRating, setHoveredRating] = useState(0);
  const [comment, setComment] = useState("");
  const [activeVideoIndex, setActiveVideoIndex] = useState(0);
  const videoRef = useRef(null);

  // Reset state when modal opens
  useEffect(() => {
    if (visible) {
      setActiveVideoIndex(0);
    }
  }, [visible]);

  // Load new video source when index changes
  useEffect(() => {
    if (videoRef.current && visible) {
      videoRef.current.load();
      videoRef.current.play().catch(() => {});
    }
  }, [activeVideoIndex, visible]);

  const handleVideoEnded = useCallback(() => {
    setActiveVideoIndex((prev) => (prev + 1) % SHOWCASE_VIDEOS.length);
  }, []);

  const handleBadgeClick = useCallback((index) => {
    setActiveVideoIndex(index);
  }, []);

  const handleSubmit = () => {
    if (rating === 0) return;

    onSubmit?.({
      rating,
      comment: comment.trim(),
      timestamp: new Date().toISOString(),
    });

    setRating(0);
    setComment("");
    onHide();
  };

  const handleSkip = () => {
    setRating(0);
    setComment("");
    onHide();
  };

  const displayRating = hoveredRating || rating;

  const getRatingLabel = (value) => {
    const labels = {
      1: "Ruim",
      2: "Regular",
      3: "Bom",
      4: "Muito Bom",
      5: "Excelente",
    };
    return labels[value] || "";
  };

  const footer = (
    <div className="modal-footer rating-modal-footer">
      <Button
        label="Pular"
        icon="pi pi-times"
        onClick={handleSkip}
        className="p-button-text"
        disabled={isLoading}
        data-evo-id="ratingmodal-btn-skip"
      />
      <Button
        label="Enviar Avaliação"
        icon="pi pi-send"
        onClick={handleSubmit}
        className="p-button-success"
        disabled={isLoading || rating === 0}
        data-evo-id="ratingmodal-btn-submit"
      />
    </div>
  );

  return (
    <ModalBase
      header={
        <div className="rating-header-custom">
          <i className="pi pi-sparkles" />
          Avalie as novidades
        </div>
      }
      visible={visible}
      onHide={handleSkip}
      footer={footer}
      size="small"
      variant="simple"
      className={`rating-modal ${isLoading ? "modal-loading" : ""}`}
      closable={true}
    >
      <div className="rating-modal-content">
        {/* Video Showcase */}
        <div className="rating-showcase">
          <div className="rating-showcase-video">
            <video
              ref={videoRef}
              className="rating-preview-video"
              autoPlay
              muted
              playsInline
              onEnded={handleVideoEnded}
            >
              <source
                src={SHOWCASE_VIDEOS[activeVideoIndex].webm}
                type="video/webm"
              />
              <source
                src={SHOWCASE_VIDEOS[activeVideoIndex].mp4}
                type="video/mp4"
              />
            </video>
          </div>

          <div className="rating-showcase-features">
            {SHOWCASE_VIDEOS.map((video, index) => (
              <span
                key={index}
                className={`rating-feature-badge ${index === activeVideoIndex ? "rating-feature-badge-active" : ""}`}
                onClick={() => handleBadgeClick(index)}
                data-evo-id={`ratingmodal-badge-${index}`}
              >
                {video.icon ? (
                  <i className={`codicon ${video.icon}`} />
                ) : (
                  video.emoji
                )}{" "}
                {video.title}
              </span>
            ))}
          </div>
        </div>

        {/* Star Rating */}
        <div className="rating-stars-section">
          <p className="rating-description">
            O que achou das novidades desta versão?
          </p>

          <div className="star-rating-container">
            <div
              className="star-rating"
              onMouseLeave={() => setHoveredRating(0)}
            >
              {[1, 2, 3, 4, 5].map((starIndex) => (
                <button
                  key={starIndex}
                  type="button"
                  className={`star-button ${starIndex <= displayRating ? "active" : ""}`}
                  onClick={() => setRating(starIndex)}
                  onMouseEnter={() => setHoveredRating(starIndex)}
                  aria-label={`${starIndex} estrela${starIndex > 1 ? "s" : ""}`}
                  data-evo-id={`ratingmodal-star-${starIndex}`}
                >
                  <i
                    className={`pi ${starIndex <= displayRating ? "pi-star-fill" : "pi-star"}`}
                  />
                </button>
              ))}
            </div>

            <span
              className={`rating-label ${displayRating > 0 ? "visible" : ""}`}
            >
              {getRatingLabel(displayRating)}
            </span>
          </div>
        </div>

        {/* Comment */}
        <div className="comment-section">
          <label htmlFor="rating-comment" className="comment-label">
            Comentário (opcional)
          </label>
          <InputTextarea
            id="rating-comment"
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            placeholder="Sugestões, problemas ou o que gostaria de ver nas próximas versões..."
            rows={3}
            maxLength={500}
            className="comment-input"
            autoResize
            data-evo-id="ratingmodal-textarea-comment"
          />
          <span className="character-count">{comment.length}/500</span>
        </div>
      </div>
    </ModalBase>
  );
};

RatingModal.propTypes = {
  visible: PropTypes.bool.isRequired,
  onHide: PropTypes.func.isRequired,
  onSubmit: PropTypes.func,
  isLoading: PropTypes.bool,
};

export default RatingModal;
